import type { IShipper, IOption } from '../_types'
import { languageOptions } from './Constants'
import { SUPPORTED_LOCALES } from '@/i18n/locales'
import type { Locale } from '@/i18n/routing'
import { mapWebSiteLanguageCodes } from './resolve-locale-field'

export const STATIC_LANGUAGE_CODES = ['uz', 'ru', 'en'] as const
export const KNOWN_LANGUAGE_CODES = ['uz', 'ru', 'en', 'kk', 'zh', 'az'] as const

export type KnownLanguageCode = (typeof KNOWN_LANGUAGE_CODES)[number]

/** Country ISO → native locale shown instead of `uz` when dynamic language is off. */
export const COUNTRY_NATIVE_LANGUAGE: Record<string, KnownLanguageCode> = {
  KZ: 'kk',
  AZ: 'az',
  CN: 'zh',
}

export function isDynamicLanguageEnabled(
  shipper?: IShipper | null
): boolean {
  return shipper?.use_dynamic_language === true
}

export function getNativeLanguageCode(
  countryIso?: string | number | null
): KnownLanguageCode {
  if (countryIso == null || countryIso === '') return 'uz'
  const key = String(countryIso).trim().toUpperCase()
  return COUNTRY_NATIVE_LANGUAGE[key] ?? 'uz'
}

/**
 * Build language switcher options from WebSettings.web_site_languages order.
 * Enriches with known icons/labels; excludes codes not in next-intl routing.
 * When dynamic language is off: ru/en + country native (and remap `uz` → native).
 */
export function getFilteredLanguageOptions(
  webSiteLanguages: IOption[],
  useDynamicLanguage: boolean,
  countryIso?: string | number | null
) {
  const codes = mapWebSiteLanguageCodes(webSiteLanguages)
  const byValue = new Map(
    (webSiteLanguages || []).map((lang) => {
      const value = lang.value === 'kz' ? 'kk' : lang.value
      return [value, lang] as const
    })
  )

  const options = codes
    .filter((code) =>
      (SUPPORTED_LOCALES as readonly string[]).includes(code)
    )
    .map((code) => {
      const known = languageOptions.find((option) => option.value === code)
      const fromSettings = byValue.get(code)
      return {
        id: known?.id ?? code,
        label: known?.label ?? fromSettings?.label ?? code.toUpperCase(),
        icon: known?.icon,
        value: code as Locale,
      }
    })

  if (useDynamicLanguage) {
    return options
  }

  const native = getNativeLanguageCode(countryIso)
  // Static mode: ru/en + country native. Treat `uz` as the local slot (remapped to native).
  const allowedStatic = new Set<string>(['ru', 'en', native, 'uz'])

  const staticOptions = options.filter((option) =>
    allowedStatic.has(option.value)
  )

  const hasUzSlot = staticOptions.some((option) => option.value === 'uz')
  const knownNative = languageOptions.find((option) => option.value === native)

  let result = staticOptions
  if (native !== 'uz' && knownNative && hasUzSlot) {
    result = staticOptions.map((option) =>
      option.value === 'uz'
        ? {
            id: knownNative.id,
            label: knownNative.label,
            icon: knownNative.icon,
            value: knownNative.value as Locale,
          }
        : option
    )
  }

  // Dedupe if settings had both uz and native (or uz remapped onto existing native).
  const seen = new Set<string>()
  return result.filter((option) => {
    if (seen.has(option.value)) return false
    seen.add(option.value)
    return true
  })
}

export function isKnownLanguageCode(value: string): value is KnownLanguageCode {
  return (KNOWN_LANGUAGE_CODES as readonly string[]).includes(value)
}

export function resolveContentLocale(
  locale: string,
  useDynamicLanguage: boolean
): string {
  if (useDynamicLanguage) return locale
  if (locale === 'en' || locale === 'ru') return locale
  return 'uz'
}

export function normalizeSeoLocale(
  locale: string,
  useDynamicLanguage: boolean
): string {
  if (useDynamicLanguage) return locale
  if (locale === 'kk' || locale === 'az' || locale === 'zh') return 'uz'
  return locale
}
